import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  Text,
} from '@chakra-ui/react';
import { CoinDuotone } from './icons/coin-duotone';
import { useSession } from 'next-auth/react';
import { colors } from 'theme/colors/colors';
import Image from 'next/image';
import { CheckList } from 'components/check-list/check-list';
import { getGamification } from 'controllers/gamification';
import { useEffect, useState } from 'react';
import {
  GamingLevelRequirementState,
  TaplioLevelNames,
} from 'types/gamification';
import { getAccount } from 'utils/sessionHelper';
import { useFirebaseUser } from 'utils/useFirebaseUser';
import PuffLoader from 'react-spinners/PuffLoader';

const TAPLIO_PROGRESS = {
  linkedInPostViews: 'views',
  linkedInPostReactions: 'reactions',
  linkedInCommentCount30: 'comments',
  linkedInNewConnections: 'connections',
  linkedInPostsPerWeek: 'posts',
};

const TAPLIO_LEVELS = {
  bronze: {
    name: 'Bronze',
    color: colors.taplioDashboard.bronze,
  },
  silver: {
    name: 'Silver',
    color: colors.taplioDashboard.silver,
  },
  gold: {
    name: 'Gold',
    color: colors.taplioDashboard.gold,
  },
  platinum: {
    name: 'Platinum',
    color: colors.taplioDashboard.platinum,
  },
  diamond: {
    name: 'Diamond',
    color: colors.taplioDashboard.diamond,
  },
};

const TAPLIO_LINKS_BY_REQUIREMENT = {
  linkedInCommentCount30: '/engage?tab=best-for-you',
  linkedInNewConnections: '/engage?tab=best-for-you',
  linkedInPostsPerWeek: 'newPost',
  linkedInPostViews: '/stats',
  linkedInPostReactions: '/stats',
};

export const TaplioLevel = () => {
  const { data: session, status } = useSession() ?? {};
  const [isLoading, setIsLoading] = useState(true);
  const isAuthenticated = useFirebaseUser();
  const [taplioLevel, setTaplioLevel] = useState<TaplioLevelNames | null>(null);
  const [taplioLevelProgress, setTaplioLevelProgress] = useState<number>(0);
  const [requirements, setRequirements] = useState<
    GamingLevelRequirementState[]
  >([]);
  const color = colors.taplioDashboard.blue;

  useEffect(() => {
    const fetchGamificationRequirements = async (account: any) => {
      const { requirements, userGamificationState, levelProgress } =
        await getGamification(account);
      setIsLoading(false);

      if (!requirements || !levelProgress || !userGamificationState) return;

      // Transform requirements into CheckListItemProps format
      let progress = 0;
      let total = 0;
      const checkListItems = requirements.requirements.map(
        (req, index): GamingLevelRequirementState => {
          let isDone = false;
          switch (req.type) {
            case 'number':
              isDone = levelProgress[req.name] >= req.value;
              break;
            case 'boolean':
              isDone = levelProgress[req.name];
              break;
            default:
              isDone = false;
              break;
          }

          if (isDone) {
            progress++;
          }
          total++;

          return {
            title: req.label,
            isDone,
            name: req.name,
            progressLabel: levelProgress[req.name]
              ? `${levelProgress[req.name] || ''} ${
                  TAPLIO_PROGRESS[req.name] || ''
                }`.trim()
              : '',
          };
        }
      );
      setTaplioLevel(userGamificationState.currentLevel);
      setTaplioLevelProgress(
        Math.ceil(Math.min((progress / total) * 100, 100))
      );
      setRequirements(checkListItems);
    };

    if (!taplioLevel) {
      const account = getAccount(session);
      if (isAuthenticated && account?.id) {
        fetchGamificationRequirements(account);
      }
    }
  }, [taplioLevel, session, isAuthenticated]);

  if (isLoading) {
    return (
      <Card flexGrow={1} flexShrink={0} w='40%'>
        <Flex justifyContent='center' alignItems='center' h='100%'>
          <PuffLoader
            color={'#0A66C2'}
            loading={true}
            cssOverride={{
              display: 'block',
              margin: '0 auto',
              borderColor: 'red',
            }}
            size={50}
          />
        </Flex>
      </Card>
    );
  }

  if (!taplioLevel) {
    return null;
  }

  return (
    <Card flexGrow={1} flexShrink={1}>
      <CardHeader>
        <Heading size='sm'>
          <Box position='relative'>
            <Flex gap={2} alignItems='center' direction='row'>
              <CoinDuotone />
              <Flex direction='row' width='100%'>
                <Text>Your Taplio level:&nbsp;</Text>
                <Text color={TAPLIO_LEVELS[taplioLevel].color}>
                  {TAPLIO_LEVELS[taplioLevel].name}
                </Text>
              </Flex>
            </Flex>
            <Box position='absolute' top='-60px' right={0}>
              <Image
                src={`/assets/dashboard/${taplioLevel}.png`}
                alt={TAPLIO_LEVELS[taplioLevel].name}
                width={120}
                height={120}
              />
            </Box>
          </Box>
        </Heading>
      </CardHeader>
      <CardBody>
        <Flex flexDirection='column' gap={6} h='100%'>
          <Box w='100%' h='3' backgroundColor='#F6F6F6' borderRadius='full'>
            <Box
              w={`${taplioLevelProgress}%`}
              h='3'
              backgroundColor={color}
              borderRadius='full'
            ></Box>
          </Box>
          <Flex
            flexGrow={1}
            flexDirection='column'
            justifyContent='space-between'
          >
            <CheckList
              items={requirements}
              linkedMap={TAPLIO_LINKS_BY_REQUIREMENT}
            />
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
};
